import React, {
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';
import ReactDom from 'react-dom';
import { Animations } from 'appkit-react';
import PropTypes from 'prop-types';
import { Loading } from '@components';
import { stopPropagation } from '@utils/common';
import { INSIGHT_FIELDS } from '@model/insights';
// import { useOutsideDisable } from '@components';

// import '@style/modules/insights/insightModal.scss';
import '../../../styles/modules/insights/insightModal.scss';
import '../../../styles/modules/insights/analysisInsightList.scss';
import '../../../styles/modules/insights/analysisInsightDetail.scss';

const BODY_ANIMATIONS = {
  IN: 'fadeInDown',
  OUT: 'fadeOutUp',
};

const LAYER_ANIMATIONS = {
  [BODY_ANIMATIONS.IN]: 'fadeIn',
  [BODY_ANIMATIONS.OUT]: 'fadeOut',
};

const InsightModal = forwardRef(
  (
    {
      children,
      changeConfig,
      rootNode,
      isFetchLoading,
      closeModalCallback,
      disableScroll,
      modalBodyClassName,
      ...restProps
    },
    ref,
  ) => {
    const rootDom = useRef(null);
    const modalRef = useRef(null);
    const [isShow, toggleShow] = useState(false);
    const [activeType, toggleActive] = useState('');
    const hide = () => {
      toggleShow(false);
    };

    const afterAnimation = () => {
      activeType === BODY_ANIMATIONS.OUT && hide();
      toggleActive(null);
    };

    const closeModal = (needCallModalCallback = false) => {
      if (restProps.isActionLoading) {
        return;
      }

      toggleActive(BODY_ANIMATIONS.OUT);
      if (needCallModalCallback || restProps.isUpdated) {
        closeModalCallback && closeModalCallback();
      }
    };

    // useOutsideDisable(modalRef, hide);

    useEffect(() => {
      rootDom.current = document.querySelector(rootNode);

      return () => {
        rootDom.current = null;
      };
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        current: modalRef.current,
        show(config) {
          toggleShow(true);
          toggleActive(BODY_ANIMATIONS.IN);
          config && changeConfig(config);
          // console.log('上一层传进来的config');
        },
        close() {
          closeModal(true);
        },
      }),
      [],
    );

    const _changeConfig = (options = {}) => {
      changeConfig({
        ...restProps,
        ...options,
      });
    };

    useEffect(() => {
      if (isShow && disableScroll) {
        document.body.style.overflow = 'hidden';
      }
      return () => {
        document.body.style.overflow = 'auto';
        document.body.style.width = 'auto';
      };
    }, [isShow, disableScroll]);

    if (!rootDom.current) {
      return null;
    }

    const containerClassName =
      isShow && disableScroll ? 'insight-modal-fixed ' : '';

    const className = `insight-modal-overlay ${
      disableScroll ? 'insight-modal-overlay-fixed' : ''
    } ${isShow ? 'insight-modal-overlay-show' : ''} ${
      restProps.field === INSIGHT_FIELDS.PRINCIPLE ? 'guiding-principle' : ''
    }`;

    return ReactDom.createPortal(
      <Animations
        effect={LAYER_ANIMATIONS[activeType]}
        active={Boolean(activeType)}
      >
        <div className={containerClassName}>
          <div /*onClick={closeModal} */ ref={modalRef} className={className}>
            {isShow && (
              <Animations
                effect={activeType}
                active={Boolean(activeType)}
                afterAnimation={afterAnimation}
              >
                <div
                  className={`insight-modal-body ${modalBodyClassName || ''}`}
                  onClick={stopPropagation}
                >
                  {isFetchLoading && (
                    <div className="insight-modal-loading-wrapper">
                      <Loading />
                    </div>
                  )}
                  <span
                    onClick={() => closeModal()}
                    className="appkiticon closeicon icon-close-fill a-font-16"
                  />
                  {children &&
                    React.cloneElement(children, {
                      ...restProps,
                      changeConfig: _changeConfig,
                      closeModal,
                      isFetchLoading,
                    })}
                </div>
              </Animations>
            )}
          </div>
        </div>
      </Animations>,
      rootDom.current,
    );
  },
);

InsightModal.propTypes = { children: PropTypes.element.isRequired };

export default InsightModal;
